/**
 * Las ofertas para el personal (the owner, 2026-11-03: «let's build a feature where we can print a pdf
 * of all active offers so i can give it to my employees»): the offers running today on the shop's
 * list, as a page of US Letter to print and hand to the people at the counter. A row for each
 * presentation at an offer: the product, the presentation, the offer (a price with how much it takes
 * off, or so many for fewer with what so many come to), the price without it, and its last day with
 * how many days are left.
 *
 * Each row is what the till charges today (`precioEnCaja`), never worked out again: a presentation
 * whose offer the till does not charge (one dearer than the list's price, the price lowered since it
 * was made; so many for fewer with no price for the ones paid) is not on it. In Spanish whatever the
 * screen's language, as the pay stubs are: it is for the staff. No cost, no margin, nothing of why an
 * offer was put on: what a cashier tells a customer. Drawn by the app's own PDF writer (`pdf.ts`), as
 * the price catalogue is.
 */
import { diasEntre, formatMoney, formatQuantity, type Centavos, type Milli } from '@abastece/shared';
import { ultimoDiaDeOferta, type Oferta, type Producto, type UnidadVenta } from '@abastece/core';
import { descuentoDe, estadoDeOferta, nombreDeLleva, precioEnCaja } from './ofertas.js';
import { DocumentoPdf, anchoDeTexto, recortar } from './pdf.js';

/** One presentation at an offer, as the till charges it today. */
export interface FilaParaElPersonal {
  productoId: string;
  /** As the shop wrote it. */
  producto: string;
  /** «Unidad», «Paquete (12)», in Spanish. */
  presentacion: string;
  /**
   * A lower price, with the list's own when there is one; or so many for fewer, each at the list's
   * price (`precio`), which is what a customer taking fewer pays.
   */
  oferta: { precio: Centavos; antes: Centavos | null } | { lleva: number; paga: number; precio: Centavos };
  /** Its last day. */
  hasta: string;
}

/**
 * «Unidad», «Paquete (12)», «Saco (100 libra)»: a presentation as the screen says it in Spanish
 * (`etiquetaPresentacion`), whatever language the screen is in. The shop's presentations are named
 * in Spanish; one it named otherwise stays as it wrote it.
 */
export function presentacionEnEspanol(u: Pick<UnidadVenta, 'nombre' | 'factor'>, unidadBase: string): string {
  const suyo = u.nombre.charAt(0).toLocaleUpperCase('es') + u.nombre.slice(1);
  if (u.factor === 1000) return suyo;
  const cuanto = formatQuantity(u.factor as Milli);
  return `${suyo} (${unidadBase === 'unidad' ? cuanto : `${cuanto} ${unidadBase}`})`;
}

/**
 * The offers running on `listaId` on `hoy`, a row for each presentation the till charges at one: by
 * product, as the shop wrote it, A to Z; each product's presentations smallest first. A product off
 * sale is not sold, and is not on it.
 */
export function filasParaElPersonal(d: {
  ofertas: readonly Oferta[];
  productos: readonly Producto[];
  unidades: readonly UnidadVenta[];
  precios: readonly { id: string; precio: Centavos }[];
  listaId: string;
  hoy: string;
}): FilaParaElPersonal[] {
  const porId = new Map(d.productos.map((p) => [p.id, p]));
  const unidades = new Map(d.unidades.map((u) => [u.id, u]));
  const deCadaUno: FilaParaElPersonal[][] = [];
  for (const o of d.ofertas) {
    const p = porId.get(o.productoId);
    if (!p?.activo || o.listaId !== d.listaId || estadoDeOferta(o, d.hoy) !== 'corre') continue;
    const filas = o.precios
      .flatMap((x) => {
        const u = unidades.get(x.unidadVentaId);
        return u?.productoId === p.id ? [u] : [];
      })
      .sort((a, b) => a.factor - b.factor)
      .flatMap((u): FilaParaElPersonal[] => {
        const caja = precioEnCaja(d.precios, d.ofertas, u, d.listaId, d.hoy);
        if (!caja || caja.ofertaId !== o.id) return [];
        return [
          {
            productoId: p.id,
            producto: p.nombre.trim(),
            presentacion: presentacionEnEspanol(u, p.unidadBase),
            oferta: caja.lleva
              ? { lleva: caja.lleva.cantidad, paga: caja.lleva.paga, precio: caja.precio }
              : { precio: caja.precio, antes: caja.precioRegular ?? null },
            hasta: ultimoDiaDeOferta(o),
          },
        ];
      });
    if (filas.length > 0) deCadaUno.push(filas);
  }
  return deCadaUno.sort((a, b) => a[0]!.producto.localeCompare(b[0]!.producto, 'es')).flat();
}

/** What the page is, at its top and at its foot. */
export const TITULO_PARA_EL_PERSONAL = 'Ofertas vigentes';
/** What a reader calls the file. */
export const tituloParaElPersonal = (negocio: string) => `${TITULO_PARA_EL_PERSONAL} de ${negocio.trim()}`;
/** For the staff, under the title: they need not do anything at the till. */
export const LA_CAJA_LAS_COBRA = 'La caja cobra cada oferta sola, hasta su último día: no hace falta cambiar ningún precio.';

/** «martes, 3 de noviembre de 2026». */
const diaLargo = (f: string) =>
  new Date(`${f}T12:00:00Z`).toLocaleDateString('es-NI', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** «dom 8 nov»: the day of the week tells the staff more than the date does. */
function diaCorto(f: string): string {
  const d = new Date(`${f}T12:00:00Z`);
  const como = (o: Intl.DateTimeFormatOptions) => d.toLocaleDateString('es-NI', { ...o, timeZone: 'UTC' });
  return `${como({ weekday: 'short' })} ${como({ day: 'numeric', month: 'short' })}`;
}

/** Under the offer's price: how much it takes off, «-30%»; under one per cent, how much money. */
function cuantoQuita(precio: Centavos, antes: Centavos): string {
  const porCiento = Math.round(descuentoDe(precio, antes) / 100);
  return porCiento >= 1 ? `-${porCiento}%` : `ahorra ${formatMoney((antes - precio) as Centavos)}`;
}

const MARGEN = 40;
const ARRIBA = 40;
/** Where what is listed must end, above the foot of the page. */
const ABAJO = 52;
const ALTO_CABECERA = 18;
/** A row: what it is on a line, and under it, smaller, how much off or what so many come to, and the days left. */
const ALTO_FILA = 30;
const LETRA = 10;
const LETRA_DEBAJO = 8;
/** The columns after the product's name, as wide as their widest: the name has the rest. */
const COLUMNA_PRESENTACION = 96;
const COLUMNA_OFERTA = 88;
const COLUMNA_NORMAL = 80;
const COLUMNA_HASTA = 76;
/** Between a column's edge and what is written in it. */
const AIRE = 6;

/** A product's rows, together as `filasParaElPersonal` lists them: they go on one page. */
function porProducto(filas: readonly FilaParaElPersonal[]): FilaParaElPersonal[][] {
  const bloques: FilaParaElPersonal[][] = [];
  for (const f of filas) {
    const ultimo = bloques.at(-1);
    if (ultimo && ultimo[0]!.productoId === f.productoId) ultimo.push(f);
    else bloques.push([f]);
  }
  return bloques;
}

/**
 * The rows on US Letter. The first page opens with the shop's name, «Ofertas vigentes» and the day,
 * and the line that the till charges them by itself; every page has the columns' names («Producto»,
 * «Presentación», «Oferta», «Precio normal», «Hasta»), then the rows, a product's never split between
 * two pages; at the foot the shop, the title, the day and «Página 1 de 2».
 *
 * A lower price is in bold with how much it takes off under it, the list's price beside it struck
 * through. So many for fewer is «3×2» in bold with what so many come to under it, «3 por C$ 40.00»,
 * and the list's price beside it, not struck: who takes fewer pays it. The last day is «dom 8 nov»,
 * with «quedan 6 días» under it, or «termina hoy».
 */
export function ofertasParaElPersonalEnPdf(filas: readonly FilaParaElPersonal[], d: { negocio: string; hoy: string }): DocumentoPdf {
  const pdf = new DocumentoPdf();
  const { ancho, alto } = pdf;
  const derecha = ancho - MARGEN;
  // The columns from the right: until when, the price without it, the offer, the presentation.
  const hastaX = derecha - COLUMNA_HASTA + AIRE;
  const finNormal = derecha - COLUMNA_HASTA - AIRE;
  const finOferta = derecha - COLUMNA_HASTA - COLUMNA_NORMAL - AIRE;
  const presentacionX = derecha - COLUMNA_HASTA - COLUMNA_NORMAL - COLUMNA_OFERTA - COLUMNA_PRESENTACION;
  const productoX = MARGEN + AIRE;
  const anchoProducto = presentacionX - productoX - AIRE;
  const dia = diaLargo(d.hoy);
  let y = 0;

  function cabeceraDeColumnas() {
    pdf.banda(MARGEN, y, derecha - MARGEN, ALTO_CABECERA, 0.92);
    const base = y + 12;
    const como = { fuente: 'negrita' as const, tamano: 8, gris: 0.25 };
    pdf.texto(productoX, base, 'Producto', como);
    pdf.texto(presentacionX, base, 'Presentación', como);
    pdf.texto(finOferta, base, 'Oferta', { ...como, alinear: 'derecha' });
    pdf.texto(finNormal, base, 'Precio normal', { ...como, alinear: 'derecha' });
    pdf.texto(hastaX, base, 'Hasta', como);
    y += ALTO_CABECERA;
  }

  function fila(f: FilaParaElPersonal) {
    const base = y + 12.5;
    const debajo = base + 11;
    const chica = { tamano: LETRA_DEBAJO, gris: 0.4 };
    pdf.texto(productoX, base, recortar(f.producto, anchoProducto, 'normal', LETRA), { tamano: LETRA });
    pdf.texto(presentacionX, base, recortar(f.presentacion, COLUMNA_PRESENTACION - AIRE, 'normal', LETRA), { tamano: LETRA, gris: 0.35 });
    const anchoOferta = COLUMNA_OFERTA - AIRE;
    if ('lleva' in f.oferta) {
      const { lleva, paga, precio } = f.oferta;
      pdf.texto(finOferta, base, nombreDeLleva({ cantidad: lleva, paga }), { fuente: 'negrita', tamano: LETRA, alinear: 'derecha' });
      const porTodas = `${lleva} por ${formatMoney((precio * paga) as Centavos)}`;
      pdf.texto(finOferta, debajo, recortar(porTodas, anchoOferta, 'normal', LETRA_DEBAJO), { ...chica, alinear: 'derecha' });
      // Who takes fewer pays the list's price: it is not struck through.
      pdf.texto(finNormal, base, formatMoney(precio), { tamano: LETRA, gris: 0.35, alinear: 'derecha' });
    } else {
      const { precio, antes } = f.oferta;
      pdf.texto(finOferta, base, formatMoney(precio), { fuente: 'negrita', tamano: LETRA, alinear: 'derecha' });
      if (antes === null) {
        pdf.texto(finNormal, base, '—', { tamano: LETRA, gris: 0.6, alinear: 'derecha' });
      } else {
        pdf.texto(finOferta, debajo, recortar(cuantoQuita(precio, antes), anchoOferta, 'normal', LETRA_DEBAJO), { ...chica, alinear: 'derecha' });
        const normal = formatMoney(antes);
        pdf.texto(finNormal, base, normal, { tamano: LETRA, gris: 0.35, alinear: 'derecha' });
        // Struck through: it is not what it is charged now.
        pdf.linea(finNormal - anchoDeTexto(normal, 'normal', LETRA), base - 3.5, finNormal, base - 3.5, { grosor: 0.7, gris: 0.35 });
      }
    }
    pdf.texto(hastaX, base, recortar(diaCorto(f.hasta), COLUMNA_HASTA - AIRE, 'normal', LETRA), { tamano: LETRA });
    const quedan = diasEntre(d.hoy, f.hasta) + 1;
    pdf.texto(hastaX, debajo, quedan <= 1 ? 'termina hoy' : `quedan ${quedan} días`, chica);
    y += ALTO_FILA;
    pdf.linea(MARGEN, y, derecha, y, { grosor: 0.4, gris: 0.86 });
  }

  // The first page: who, what and when, and a word for the staff, over the columns.
  pdf.nuevaPagina();
  y = ARRIBA;
  pdf.texto(MARGEN, y + 18, recortar(d.negocio.trim(), derecha - MARGEN, 'negrita', 18), { fuente: 'negrita', tamano: 18 });
  y += 36;
  pdf.texto(MARGEN, y, recortar(`${TITULO_PARA_EL_PERSONAL} · ${dia}`, derecha - MARGEN, 'normal', 10), { tamano: 10, gris: 0.3 });
  y += 14;
  pdf.texto(MARGEN, y, recortar(LA_CAJA_LAS_COBRA, derecha - MARGEN, 'normal', 9), { tamano: 9, gris: 0.45 });
  y += 16;
  cabeceraDeColumnas();
  if (filas.length === 0) pdf.texto(productoX, y + 16, 'Hoy no corre ninguna oferta.', { tamano: LETRA, gris: 0.4 });

  for (const bloque of porProducto(filas)) {
    if (y + bloque.length * ALTO_FILA > alto - ABAJO) {
      pdf.nuevaPagina();
      y = ARRIBA;
      cabeceraDeColumnas();
    }
    for (const f of bloque) fila(f);
  }

  // The foot of each page, now that it is known how many there are.
  const pie = `${d.negocio.trim()} · ${TITULO_PARA_EL_PERSONAL} · ${dia}`;
  for (let i = 0; i < pdf.cuantasPaginas; i++) {
    pdf.irAPagina(i);
    pdf.linea(MARGEN, alto - 34, derecha, alto - 34, { grosor: 0.4, gris: 0.8 });
    pdf.texto(MARGEN, alto - 22, recortar(pie, derecha - MARGEN - 90, 'normal', 8), { tamano: 8, gris: 0.45 });
    pdf.texto(derecha, alto - 22, `Página ${i + 1} de ${pdf.cuantasPaginas}`, { tamano: 8, gris: 0.45, alinear: 'derecha' });
  }
  return pdf;
}
